import { useBlockType } from "components/Dashboard/DoctorDashboard/notes-and-forms/components/template-editor/components/block-type/contexts/BlockTypeContext";
import { useSubscription } from "contexts/subscription-context";
import DOMPurify from "dompurify";
import useId from "hooks/useId";
import type { ChangeEventHandler } from "react";
import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import "react-quill/dist/quill.snow.css";
import getClasses from "utils/getClasses";
import styles from "./RichTextEditor.module.scss";
import QuillToolbar from "./components/quill-toolbar/QuillToolbar";
import { FORMATS, MODULES } from "./configs.js";

type Props = {
  editorClassName?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (arg: string) => void;
  onUpload?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  hideToolbar?: boolean;
} & Omit<
  ComponentPropsWithoutRef<typeof ReactQuill>,
  "onChange" | "readOnly" | "value"
>;

const RichTextEditor = ({
  className,
  editorClassName,
  value,
  onChange,
  placeholder,
  disabled,
  readOnly = false,
  onUpload,
  hideToolbar,
  ...props
}: Props) => {
  const { canAccessFeature } = useSubscription();
  const { isDownload } = useBlockType();

  const id = useId("rich-text-editor");

  const [localValue, setLocalValue] = useState(value ?? "");

  // keep track of the upstream data change
  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  /**
   * onChange need to handle both local and upstream data change since sometimes there will be latency
   * @param arg next value
   */
  const handleOnChange = useCallback(
    (arg: string) => {
      setLocalValue(arg);
      return onChange && onChange(arg);
    },
    [onChange],
  );

  if (isDownload) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(value ?? "", {
            USE_PROFILES: { html: true },
          }),
        }}
      />
    );
  }

  return (
    <div
      className={getClasses(
        styles["rich-text-editor"],
        className,
        disabled && styles["rich-text-editor--disabled"],
        readOnly && styles["rich-text-editor--readOnly"],
      )}
    >
      <QuillToolbar
        id={id}
        onUpload={onUpload}
        isOnComplete={canAccessFeature("note-file-upload")}
      />
      <ReactQuill
        modules={{
          ...MODULES,
          toolbar: { ...MODULES.toolbar, container: "#" + id },
        }}
        className={editorClassName}
        formats={FORMATS}
        bounds={".app"}
        onChange={handleOnChange}
        value={localValue}
        readOnly={readOnly || disabled}
        // theme={!readOnly && !hideToolbar ? "snow" : "bubble"}
        placeholder={readOnly && !disabled ? "" : placeholder}
        {...props}
      />
    </div>
  );
};

export default RichTextEditor;
